import { ComponentProps, forwardRef, ReactNode, RefObject } from 'react';
import { cn } from '../../utils/className';

interface TableWrapperProps extends ComponentProps<'div'> {
  /** The scrollable table content */
  children: ReactNode;
  /** Optional footer content (e.g., pagination) that stays fixed outside scroll */
  footer?: ReactNode;
  /** Optional ref for the scrollable container */
  scrollContainerRef?: RefObject<HTMLDivElement | null>;
}

/**
 * TableWrapper - Container for tables with optional fixed footer
 *
 * Provides a bordered container with:
 * - Rounded corners
 * - Scrollable table area
 * - Optional fixed footer (for pagination) outside scroll area
 *
 * @example
 * ```tsx
 * <TableWrapper footer={<TablePagination {...props} />}>
 *   <Table>
 *     <TableHeader>...</TableHeader>
 *     <TableBody>...</TableBody>
 *   </Table>
 * </TableWrapper>
 * ```
 */
export const TableWrapper = forwardRef<HTMLDivElement, TableWrapperProps>(
  ({ children, footer, scrollContainerRef, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          `border-border-secondary flex min-h-0 w-full flex-1 flex-col
          overflow-hidden border bg-white`,
          className
        )}
        {...props}
      >
        {/* Scrollable table area - flex-1 to fill available space */}
        <div
          ref={scrollContainerRef}
          className='min-h-0 flex-1 overflow-auto'
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--color-neutral-300) transparent',
          }}
        >
          {children}
        </div>

        {/* Optional fixed footer (e.g., pagination) */}
        {footer && (
          <div
            className='bg-surface-primary-support border-border-secondary
              shrink-0 border-t'
          >
            {footer}
          </div>
        )}
      </div>
    );
  }
);
TableWrapper.displayName = 'TableWrapper';
